import React, { useEffect, useRef, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Trash2, Download } from "lucide-react";
import { assetApi, auditApi, documentApi, orgApi } from "@/lib/entities";
import { useAuth } from "@/contexts/AuthContext";
import { PageHeader, PageShell } from "@/components/PageHeader";
import { SectionHeader } from "@/components/SectionHeader";
import { DemoChip, StatusPill } from "@/components/Chips";
import { RoleGate } from "@/components/RoleGate";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { StatusChangeDialog } from "@/pages/fleet/StatusChangeDialog";
import { AssetFormDialog } from "@/pages/fleet/AssetFormDialog";
import VehicleOperationsTab from "@/pages/fleet/tabs/VehicleOperationsTab";
import { ArchiveConfirmModal, useArchive } from "@/components/ActionMenu";
import { apiError } from "@/lib/errors";
import { assetDisplay, assetLegacyTag } from "@/lib/assetDisplay";

const READ_ROLES = ["super_admin", "leasing_sales", "ops_dispatch", "accounting", "maintenance"];
const WRITE_ROLES = ["super_admin", "ops_dispatch", "maintenance"];

// Curated document types most useful on an Asset record. Mirrors the
// pattern established by the F3 Driver Documents tab.
const DOC_TYPE_OPTIONS = [
  ["registration", "Registration"],
  ["title",        "Title"],
  ["insurance",    "Insurance"],
  ["coi",          "Certificate of Insurance"],
  ["inspection",   "Inspection"],
  ["invoice",      "Invoice"],
  ["floorplan",    "Floor Plan"],
  ["manual",       "Manual"],
  ["photo",        "Photo"],
  ["other",        "Other"],
];

const fmt$ = (v, isPct) => {
  if (v == null || v === "") return "—";
  return isPct ? `${v}%` : `$${v}`;
};
const boolLabel = (v) => (v == null ? null : v ? "Yes" : "No");

// Phase 2 (2026-09-10) — rate_defaults keys whose UI has been removed
// from Coach-level surfaces. Values remain in the DB per Owner Decision
// 3 but are no longer displayed in Fleet Detail or the Asset edit form.
// Standard rates for these services now live in the Master Rate Catalog
// (Admin → Quote Rates).
// RC10b (2026-10-12) — Owner Item 2: lease_amount joins this set so the
// Lease Amount field is hidden from Fleet Overview and Edit UIs; the
// column is preserved in the DB, and new quotes/agreements use the
// centralized Quote Rates catalog or the frozen Quote/Contract snapshot.
const DORMANT_COACH_RATE_KEYS = new Set([
  "weekly_rate",
  "generator_amount",
  "satellite_amount",
  "internet_amount",
  "cleaning_amount",
  "other_amount",
  "lease_amount",
]);

export default function FleetDetail() {
  const { id } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user, isAdmin, can } = useAuth();
  const [asset, setAsset] = useState(null);
  const [history, setHistory] = useState([]);
  const [activity, setActivity] = useState([]);
  const [orgs, setOrgs] = useState([]);
  const [statusOpen, setStatusOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  // F10 · canonical permission (Admin auto-inherits). Replaces the
  // previous hardcoded `WRITE_ROLES.includes(user?.role)` gate.
  const canWrite = can("fleet.edit");
  // F10 · asset activity mirrors the /api/audit-logs admin-tier
  // requirement (`require_roles("super_admin","admin")`). We use the
  // canonical `isAdmin` resolver from AuthContext — same resolver
  // that lights up every non-ROOT permission for Admin users — so
  // Admin, Super Admin, and future role classes flagged `is_admin`
  // all light up correctly without a hardcoded role string.
  const canSeeActivity = !!isAdmin;
  const tab = searchParams.get("tab") || "overview";
  const setTab = (v) => {
    const next = new URLSearchParams(searchParams);
    if (v && v !== "overview") next.set("tab", v);
    else next.delete("tab");
    setSearchParams(next, { replace: true });
  };

  const load = async () => {
    try {
      const a = await assetApi.get(id);
      setAsset(a);
      const h = await assetApi.history(id).catch(() => []);
      setHistory(h);
      if (canSeeActivity) {
        auditApi.forEntity("asset", id).then(setActivity).catch(() => {});
      }
    } catch (e) {
      toast.error("Asset not found");
    }
  };

  useEffect(() => {
    load(); /* eslint-disable-next-line */
  }, [id]);

  useEffect(() => {
    if (!canWrite) return;
    orgApi.list({ page_size: 200 }).then((d) => setOrgs(d.results || [])).catch(() => {});
  }, [canWrite]);

  const uploadPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      await assetApi.uploadPhoto(id, file, false);
      toast.success("Photo uploaded");
      load();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  if (!asset) {
    return (
      <PageShell>
        {/* RC4 · shared SectionHeader adoption landmark (sr-only until full visual migration lands progressively). */}
        <SectionHeader testid="page-section-landmark" className="sr-only">Fleet Asset Detail</SectionHeader>
        <div className="text-steel text-sm">Loading asset…</div>
      </PageShell>
    );
  }

  const isTrailer = asset.asset_type === "trailer";
  const orgName = orgs.find((o) => o.id === asset.owner_organization_id)?.display_name;

  return (
    <RoleGate allow={READ_ROLES}>
      <PageShell>
        <PageHeader
          testid="page-fleet-detail"
          backTo={{ label: "Back to Fleet", to: "/fleet" }}
          title={
            <span className="inline-flex items-center gap-3">
              {assetDisplay(asset)}
              {asset.is_demo && <DemoChip />}
            </span>
          }
          subtitle={
            <span className="inline-flex items-center gap-2">
              <StatusPill status={asset.operational_status} />
              <span className="text-steel uppercase tracking-wide text-[11px]">
                {asset.asset_type.replace(/_/g, " ")}
              </span>
            </span>
          }
          right={
            canWrite && (
              <div className="flex items-center gap-2">
                <button
                  data-testid="asset-edit-btn"
                  onClick={() => setEditOpen(true)}
                  className="px-3 py-1.5 text-[12.5px] border border-coach-blue text-coach-blue rounded-sm hover:bg-coach-blue hover:text-white"
                >
                  Edit Asset
                </button>
                <button
                  data-testid="asset-change-status-btn"
                  onClick={() => setStatusOpen(true)}
                  className="px-3 py-1.5 text-[12.5px] font-medium bg-coach-blue hover:bg-deep-road-navy text-white rounded-sm"
                >
                  Change status
                </button>
              </div>
            )
          }
        />

        <Tabs value={tab} onValueChange={setTab} className="mt-2">
          <TabsList data-testid="asset-tabs" className="bg-white border border-coach-silver rounded-sm w-full justify-start overflow-x-auto">
            {[
              ["overview", "Overview"],
              ["configuration", "Configuration"],
              ["rates", "Rates"],
              ...(asset.asset_type !== "trailer" ? [["operations", "Vehicle Operations"]] : []),
              ["documents", "Documents"],
              ["photos", "Photos"],
              ["status-history", "Status History"],
              ["activity", "Activity"],
            ].map(([k, l]) => (
              <TabsTrigger
                key={k}
                value={k}
                data-testid={`asset-tab-${k}`}
                className="data-[state=active]:bg-coach-blue data-[state=active]:text-white"
              >
                {l}
              </TabsTrigger>
            ))}
          </TabsList>

          {/* ── Overview ────────────────────────────────────────────── */}
          <TabsContent value="overview" className="mt-4">
            <div className="grid md:grid-cols-2 gap-4">
              <Card title="Identity" testid="asset-card-identity">
                <Row k="Year / Make / Model" v={[asset.year, asset.make, asset.model].filter(Boolean).join(" ") || "—"} />
                {!isTrailer && <Row k="Chassis" v={asset.chassis || "—"} />}
                {!isTrailer && <Row k="Converter" v={asset.converter || "—"} />}
                <Row k="VIN" v={<span className="font-mono">{asset.vin || "—"}</span>} />
                <Row k="License Plate Number" v={asset.license_plate || "—"} />
                <Row
                  k="Registration"
                  v={
                    [asset.registration_state, asset.registration_expiration]
                      .filter(Boolean)
                      .join(" · ") || "—"
                  }
                />
                {!isTrailer && (
                  <>
                    <Row k="Engine Make / Model" v={asset.engine_make_model || "—"} />
                    <Row k="Engine Serial Number"
                         v={<span className="font-mono text-[12px]">{asset.engine_serial || "—"}</span>} />
                    <Row k="Door Code"
                         v={<span data-testid="asset-door-code" className="font-mono text-[12px]">{asset.door_code || "—"}</span>} />
                  </>
                )}
              </Card>

              <Card title="Operations" testid="asset-card-operations">
                <Row k="Owner" v={orgName || "—"} />
                <Row k="Ownership" v={asset.ownership_type || "—"} />
                <Row k="Active" v={asset.active ? "Yes" : "No"} />
                <Row k="Status" v={<StatusPill status={asset.operational_status} />} />
                {/* RC10j-10-14 · Primary Assigned Driver row.
                    Clickable link when driver exists and is not missing. */}
                <Row
                  k="Primary Driver"
                  v={
                    <span data-testid="asset-primary-driver">
                      {!asset.primary_driver_id && <span className="text-steel">—</span>}
                      {asset.primary_driver_id && asset.primary_driver_display?.missing && (
                        <span className="text-maintenance-orange">[Missing Driver]</span>
                      )}
                      {asset.primary_driver_id && !asset.primary_driver_display?.missing && (
                        <Link
                          data-testid="asset-primary-driver-link"
                          to={`/drivers/${asset.primary_driver_id}`}
                          className="text-coach-blue hover:underline"
                        >
                          {asset.primary_driver_display?.label || asset.primary_driver_id}
                        </Link>
                      )}
                    </span>
                  }
                />
              </Card>

              {(asset.quote_description || asset.schedule_description) && (
                <Card title="Descriptions" testid="asset-card-descriptions">
                  {asset.quote_description && (
                    <div className="mb-3">
                      <div className="text-[10.5px] uppercase tracking-widest text-steel mb-1">
                        Quote Description
                      </div>
                      <div className="text-[13px] text-carbon whitespace-pre-wrap">
                        {asset.quote_description}
                      </div>
                    </div>
                  )}
                  {asset.schedule_description && (
                    <div>
                      <div className="text-[10.5px] uppercase tracking-widest text-steel mb-1">
                        Schedule Description
                      </div>
                      <div className="text-[13px] text-carbon whitespace-pre-wrap">
                        {asset.schedule_description}
                      </div>
                    </div>
                  )}
                </Card>
              )}

              <Card title="Notes" testid="asset-card-notes">
                <NotesInlineEditor
                  assetId={asset.id}
                  initial={asset.notes || ""}
                  canWrite={canWrite}
                  onSaved={load}
                />
              </Card>
            </div>
          </TabsContent>

          {/* ── Configuration ───────────────────────────────────────── */}
          <TabsContent value="configuration" className="mt-4">
            {isTrailer ? (
              <div className="bg-white border border-coach-silver rounded-md p-5 text-[13px] text-steel">
                Configuration details do not apply to trailers.
              </div>
            ) : (
              <div className="space-y-4">
                <div className="bg-white border border-coach-silver rounded-md p-5 grid grid-cols-2 md:grid-cols-3 gap-4 text-[13px]">
                  {[
                    ["Bunks", asset.config?.bunk_count],
                    ["Slides", asset.config?.slide_count],
                    ["TVs", asset.config?.tv_count],
                    ["Fresh Water (gal)", asset.config?.fresh_water_gal],
                    ["Gray Water (gal)", asset.config?.gray_water_gal],
                    ["Black Water (gal)", asset.config?.black_water_gal],
                    // Phase 2 (2026-09-10) — removed from operator-facing UI:
                    // Star Room, Front Lounge, Rear Lounge, Restroom, Satellite,
                    // Wi-Fi, Generator, Shore Power. Backend fields remain
                    // dormant per Owner Decision 3 — no data touch.
                    ["Shower", boolLabel(asset.config?.shower)],
                    ["Washer / Dryer", boolLabel(asset.config?.washer_dryer)],
                    ["Refrigerator", boolLabel(asset.config?.refrigerator)],
                    ["Freezer", boolLabel(asset.config?.freezer)],
                    ["Microwave", boolLabel(asset.config?.microwave)],
                    ["Stovetop", boolLabel(asset.config?.stovetop)],
                    ["Starlink", boolLabel(asset.config?.starlink)],
                  ].map(([k, v]) => (
                    <div key={k} className="border-b border-coach-silver/50 pb-2">
                      <div className="text-[10.5px] uppercase tracking-widest text-steel">{k}</div>
                      <div className="text-carbon">{v ?? "—"}</div>
                    </div>
                  ))}
                </div>

                {(asset.config?.amenities?.length > 0 || asset.config?.custom_notes) && (
                  <div className="bg-white border border-coach-silver rounded-md p-5 space-y-3">
                    {asset.config?.amenities?.length > 0 && (
                      <div>
                        <div className="text-[10.5px] uppercase tracking-widest text-steel mb-2">
                          Additional Amenities
                        </div>
                        <div className="flex flex-wrap gap-1.5">
                          {asset.config.amenities.map((a, i) => (
                            <span
                              key={`${a}-${i}`}
                              className="inline-block text-[11.5px] px-2 py-0.5 rounded-sm border border-coach-silver bg-road-white text-carbon"
                            >
                              {a}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                    {asset.config?.custom_notes && (
                      <div>
                        <div className="text-[10.5px] uppercase tracking-widest text-steel mb-1">
                          Configuration Notes
                        </div>
                        <div className="text-[13px] text-carbon whitespace-pre-wrap">
                          {asset.config.custom_notes}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </TabsContent>

          {/* ── Rates ───────────────────────────────────────────────── */}
          <TabsContent value="rates" className="mt-4">
            {/*
              Phase 2 (2026-09-10) — Coach-level rate UI is limited to the
              per-Coach rates that legitimately vary by unit (daily,
              monthly, mileage, lease, commission). Standard service rates
              (weekly cleaning, generator, satellite, internet, cleaning,
              other) moved to Admin → Quote Rates. Backend fields remain
              dormant per Owner Decision 3 — historical values are
              preserved untouched.
            */}
            <div className="bg-white border border-coach-silver rounded-md p-5 grid grid-cols-2 md:grid-cols-3 gap-4 text-[13px]"
                 data-testid="asset-rates-grid">
              {Object.entries(asset.rate_defaults || {})
                .filter(([k]) => !DORMANT_COACH_RATE_KEYS.has(k))
                .map(([k, v]) => (
                  <div key={k} className="border-b border-coach-silver/50 pb-2">
                    <div className="text-[10.5px] uppercase tracking-widest text-steel">
                      {k.replace(/_/g, " ")}
                    </div>
                    <div className="text-carbon font-mono">
                      {fmt$(v, k.includes("percent"))}
                    </div>
                  </div>
                ))}
            </div>
          </TabsContent>

          {/* ── Documents ───────────────────────────────────────────── */}
          <TabsContent value="documents" className="mt-4">
            <AssetDocumentsPanel assetId={id} canWrite={canWrite} typeOptions={DOC_TYPE_OPTIONS} />
          </TabsContent>

          <TabsContent value="operations" className="mt-4">
            <VehicleOperationsTab assetId={id} />
          </TabsContent>

          <TabsContent value="photos" className="mt-4">
            <div className="bg-white border border-coach-silver rounded-md p-4 space-y-3">
              {canWrite && (
                <div>
                  <div className="text-[11px] uppercase tracking-wide text-steel mb-1">Upload photo</div>
                  <input type="file" accept="image/*" data-testid="asset-photo-upload" onChange={uploadPhoto} />
                </div>
              )}
              {(asset.photos || []).length === 0 ? (
                <div className="text-[12.5px] text-steel">No photos yet.</div>
              ) : (
                <ul className="text-[13px]">
                  {asset.photos.map((p) => (
                    <li key={p.id} data-testid={`asset-photo-${p.id}`} className="border-b border-coach-silver/50 py-1.5">
                      {p.title} <span className="text-steel text-[11px]">· {p.created_at?.slice(0,10)}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </TabsContent>

          <TabsContent value="status-history" className="mt-4">
            <div className="bg-white border border-coach-silver rounded-md" data-testid="asset-status-history">
              {history.length === 0 && (
                <div className="p-4 text-[12.5px] text-steel">No status changes yet.</div>
              )}
              {history.map((h) => (
                <div
                  key={h.id}
                  data-testid={`status-history-${h.id}`}
                  className="px-4 py-2 border-b border-coach-silver/60 last:border-0 flex items-center gap-3"
                >
                  <StatusPill status={h.from_status || "unknown"} />
                  <span className="text-steel text-[11px]">→</span>
                  <StatusPill status={h.to_status} />
                  <span className="ml-4 text-[12.5px] text-carbon">{h.reason || <em className="text-steel">no reason</em>}</span>
                  <span className="ml-auto text-[11px] text-steel">{h.effective_at?.replace("T", " ").slice(0, 16)}</span>
                </div>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="activity" className="mt-4">
            {!canSeeActivity ? (
              <div className="text-[12px] text-steel">Activity log is not available for your role.</div>
            ) : activity.length === 0 ? (
              <div className="text-[12px] text-steel">No recorded activity.</div>
            ) : (
              <div className="bg-white border border-coach-silver rounded-md">
                {activity.map((a) => (
                  <div key={a.id} className="px-4 py-2 border-b border-coach-silver/60 last:border-0 text-[12.5px]">
                    <span className="text-carbon font-medium">{a.action}</span>
                    <span className="text-steel"> · {a.actor_email || "system"} · {a.ts}</span>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>

        {statusOpen && (
          <StatusChangeDialog
            open={statusOpen}
            asset={asset}
            onClose={() => setStatusOpen(false)}
            onDone={() => {
              setStatusOpen(false);
              load();
            }}
          />
        )}

        {editOpen && (
          <AssetFormDialog
            open={editOpen}
            orgs={orgs}
            initial={asset}
            onClose={() => setEditOpen(false)}
            onSaved={() => {
              setEditOpen(false);
              load();
            }}
          />
        )}
      </PageShell>
    </RoleGate>
  );
}

/* ── Documents panel (F6 — mirrors DriverDocumentsPanel) ───────── */

function AssetDocumentsPanel({ assetId, canWrite, typeOptions }) {
  const [docs, setDocs] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [docType, setDocType] = useState("registration");
  const [title, setTitle] = useState("");
  const [notes, setNotes] = useState("");
  const fileInputRef = useRef(null);

  const load = async () => {
    try {
      const rows = await documentApi.list({
        entity_type: "asset",
        entity_id: assetId,
        limit: 200,
      });
      const active = (rows || [])
        .filter((r) => !r.is_archived)
        .sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""));
      setDocs(active);
    } catch {
      toast.error("Failed to load documents");
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [assetId]);

  const archive = useArchive({
    entityLabel: "Document",
    apiArchive: (docId) => documentApi.archive(docId),
    onSuccess: async () => { toast.success("Document archived"); await load(); },
  });

  const handleUpload = async (file) => {
    if (!file) return;
    if (!title.trim()) {
      toast.error("Add a title for the document");
      return;
    }
    setUploading(true);
    try {
      await documentApi.upload({
        file,
        title: title.trim(),
        document_type: docType,
        entity_type: "asset",
        entity_id: assetId,
        notes: notes || undefined,
      });
      toast.success("Document uploaded");
      setTitle("");
      setNotes("");
      if (fileInputRef.current) fileInputRef.current.value = "";
      await load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setUploading(false);
    }
  };

  const download = async (doc) => {
    try {
      const url = await documentApi.signedUrl(doc.id);
      window.open(url, "_blank");
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  return (
    <div className="space-y-4" data-testid="asset-documents-panel">
      {canWrite && (
        <div className="bg-white border border-coach-silver rounded-md p-4">
          <div className="text-[11.5px] font-medium uppercase tracking-wide text-steel mb-2">
            Upload Document
          </div>
          <div className="grid md:grid-cols-4 gap-2 text-[13px]">
            <label className="block md:col-span-2">
              <div className="text-[11px] text-steel">Title</div>
              <input
                data-testid="asset-doc-upload-title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="e.g. Registration 2026"
                className="w-full border border-coach-silver rounded-sm px-2.5 py-1.5"
              />
            </label>
            <label className="block">
              <div className="text-[11px] text-steel">Type</div>
              <select
                data-testid="asset-doc-upload-type"
                value={docType}
                onChange={(e) => setDocType(e.target.value)}
                className="w-full border border-coach-silver rounded-sm px-2.5 py-1.5 bg-white"
              >
                {typeOptions.map(([k, l]) => (
                  <option key={k} value={k}>{l}</option>
                ))}
              </select>
            </label>
            <label className="block">
              <div className="text-[11px] text-steel">Notes (optional)</div>
              <input
                data-testid="asset-doc-upload-notes"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                className="w-full border border-coach-silver rounded-sm px-2.5 py-1.5"
              />
            </label>
          </div>
          <div className="mt-3 flex items-center gap-3">
            <input
              ref={fileInputRef}
              type="file"
              data-testid="asset-doc-upload-file"
              accept="application/pdf,image/jpeg,image/png"
              disabled={uploading}
              onChange={(e) => handleUpload(e.target.files?.[0])}
              className="text-[12px]"
            />
            {uploading && <span className="text-[12px] text-steel">Uploading…</span>}
          </div>
        </div>
      )}

      <div className="bg-white border border-coach-silver rounded-md">
        {docs === null ? (
          <div className="p-6 text-center text-steel text-[13px]">Loading…</div>
        ) : docs.length === 0 ? (
          <div className="p-6 text-center text-steel text-[13px]" data-testid="asset-documents-empty">
            No coach documents on file yet.
          </div>
        ) : (
          <table className="w-full text-[13px]" data-testid="asset-documents-table">
            <thead className="text-[10.5px] uppercase tracking-widest text-steel">
              <tr className="border-b border-coach-silver/60">
                <th className="text-left px-4 py-2">Title</th>
                <th className="text-left px-4 py-2">Type</th>
                <th className="text-left px-4 py-2 hidden md:table-cell">Uploaded</th>
                <th className="px-4 py-2 w-32 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {docs.map((d) => {
                const typeLabel = typeOptions.find(([k]) => k === d.document_type)?.[1] || d.document_type;
                return (
                  <tr key={d.id} data-testid={`asset-doc-row-${d.id}`}
                      className="border-b border-coach-silver/60 last:border-0 hover:bg-road-white">
                    <td className="px-4 py-2">
                      <div className="text-carbon font-medium">{d.title}</div>
                      {d.notes && <div className="text-[11.5px] text-steel">{d.notes}</div>}
                    </td>
                    <td className="px-4 py-2 text-steel">{typeLabel}</td>
                    <td className="px-4 py-2 text-steel hidden md:table-cell">
                      {d.created_at ? d.created_at.slice(0, 10) : "—"}
                    </td>
                    <td className="px-4 py-2 text-right">
                      <button
                        data-testid={`asset-doc-download-${d.id}`}
                        onClick={() => download(d)}
                        className="inline-flex items-center gap-1 text-[12px] text-coach-blue hover:underline mr-3"
                      >
                        <Download size={12} /> Download
                      </button>
                      {canWrite && (
                        <button
                          data-testid={`asset-doc-archive-${d.id}`}
                          onClick={() => archive.request(d)}
                          className="inline-flex items-center gap-1 text-[12px] text-critical-red hover:underline"
                        >
                          <Trash2 size={12} /> Delete
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
      <ArchiveConfirmModal {...archive.modalProps} />
    </div>
  );
}

const Card = ({ title, children, testid }) => (
  <div className="bg-white border border-coach-silver rounded-md p-4" data-testid={testid}>
    <div className="text-[10.5px] uppercase tracking-widest text-steel mb-3">{title}</div>
    {children}
  </div>
);

const Row = ({ k, v }) => (
  <div className="flex items-center justify-between py-1.5 border-b border-coach-silver/50 last:border-0">
    <div className="text-[11px] uppercase tracking-wide text-steel">{k}</div>
    <div className="text-[13px] text-carbon">{v}</div>
  </div>
);

// Phase 2 · Fleet Detail Notes inline editor. Uses the existing
// `PATCH /api/assets/{id}` endpoint (no new backend surface).
function NotesInlineEditor({ assetId, initial, canWrite, onSaved }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(initial);
  const [busy, setBusy] = useState(false);
  useEffect(() => { setDraft(initial); }, [initial]);
  if (!editing) {
    return (
      <div className="flex items-start justify-between gap-3">
        <div className="text-[13px] text-carbon whitespace-pre-wrap flex-1"
             data-testid="asset-notes-view">
          {initial || "—"}
        </div>
        {canWrite && (
          <button type="button"
            data-testid="asset-notes-edit"
            onClick={() => setEditing(true)}
            className="text-[12px] text-coach-blue hover:underline shrink-0">
            Edit
          </button>
        )}
      </div>
    );
  }
  const save = async () => {
    setBusy(true);
    try {
      await assetApi.patch(assetId, { notes: draft.trim() || null });
      toast.success("Notes updated");
      setEditing(false);
      if (onSaved) await onSaved();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="space-y-2">
      <textarea
        data-testid="asset-notes-input"
        rows={4}
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        className="w-full border border-coach-silver rounded-sm px-2.5 py-1.5 text-[13px]"
        style={{ color: "var(--fg-primary)", background: "var(--bg-surface)" }}
      />
      <div className="flex items-center justify-end gap-2">
        <button type="button" disabled={busy}
          data-testid="asset-notes-cancel"
          onClick={() => { setDraft(initial); setEditing(false); }}
          className="px-3 py-1 border border-coach-silver rounded-sm text-[12px] text-steel">
          Cancel
        </button>
        <button type="button" disabled={busy}
          data-testid="asset-notes-save"
          onClick={save}
          className="px-3 py-1 bg-coach-blue text-white rounded-sm text-[12px] font-medium disabled:opacity-50">
          {busy ? "Saving…" : "Save"}
        </button>
      </div>
    </div>
  );
}
